import * as DialogPrimitive from "@radix-ui/react-dialog";
import { CheckCircle2, Clipboard, KeyRound, Loader2, ShieldAlert, Trash2, X } from "lucide-react";
import type {
  PortalIdentityTone,
  TenantOidcProviderDraft,
  TenantOidcSettingsViewProps,
} from "../../contracts/tenant-identity";
import { cn } from "../../lib/utils";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../internal/primitives/accordion";
import { Button } from "../../internal/primitives/button";
import { Input } from "../../internal/primitives/input";
import { Label } from "../../internal/primitives/label";
import { Switch } from "../../internal/primitives/switch";
import { ContentFrame } from "../shell/ContentFrame";
import { WorkspaceHeader } from "../shell/WorkspaceHeader";

const toneClasses: Record<PortalIdentityTone, string> = {
  neutral: "border-border bg-muted/40 text-muted-foreground",
  info: "border-primary/30 bg-primary/10 text-primary",
  success: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  warning: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  danger: "border-destructive/30 bg-destructive/10 text-destructive",
};

function FieldError({ children }: { children?: string | null }) {
  return children ? <p className="text-xs text-destructive">{children}</p> : null;
}

function StatusBadge({ label, tone }: { label: string; tone: PortalIdentityTone }) {
  return <span className={cn("inline-flex rounded-control border px-2 py-0.5 text-[10px] font-black uppercase tracking-wider", toneClasses[tone])}>{label}</span>;
}

export function TenantOidcSettingsView({
  title,
  description,
  loginUrlLabel,
  loginUrl,
  canView,
  canManage,
  loading,
  provider,
  clientSettings,
  draft,
  validation,
  pendingAction,
  confirmation,
  roleMappings,
  onDraftChange,
  onSaveDraft,
  onValidateProvider,
  onActivateProvider,
  onDisableProvider,
  onDeleteProvider,
  onCopyLoginUrl,
  onCopyCallbackUrl,
  onRefresh,
}: TenantOidcSettingsViewProps) {
  const patch = (value: Partial<TenantOidcProviderDraft>) => onDraftChange({ ...draft, ...value });
  const busy = pendingAction !== null;
  const secretInputDisabled = Boolean(provider?.secretConfigured) && !draft.replaceSecret;

  if (!canView) {
    return (
      <ContentFrame layout="document">
        <div className="flex min-h-64 items-center justify-center">
          <div className="max-w-md text-center">
            <ShieldAlert className="mx-auto h-8 w-8 text-muted-foreground" aria-hidden="true" />
            <h2 className="mt-3 text-lg font-black">Identity provider access is restricted</h2>
            <p className="mt-1 text-sm text-muted-foreground">Your tenant role does not include permission to view identity-provider settings.</p>
          </div>
        </div>
      </ContentFrame>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <WorkspaceHeader
        eyebrow="Tenant identity"
        title={title}
        description={description}
        actions={[{ id: "refresh", label: "Refresh", disabled: loading || busy }]}
        onAction={onRefresh}
      />
      <ContentFrame layout="document" bodyClassName="overflow-y-auto">
        <div className="space-y-5">
          {loading ? (
            <div className="flex min-h-48 items-center justify-center text-sm text-muted-foreground" role="status">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> Loading identity-provider settings…
            </div>
          ) : (
            <>
              <section className="rounded-panel border border-border bg-card" aria-labelledby="tenant-oidc-provider-heading">
                <div className="flex flex-wrap items-start justify-between gap-4 border-b border-border px-5 py-4">
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">OIDC connection</p>
                    <h2 id="tenant-oidc-provider-heading" className="mt-0.5 text-base font-black">{provider ? provider.displayName : "Connect an identity provider"}</h2>
                    <p className="mt-1 text-xs text-muted-foreground">Authentication is brokered through the platform. Tenant membership rules still apply.</p>
                  </div>
                  {provider ? (
                    <div className="flex flex-wrap gap-2">
                      <StatusBadge label={provider.desiredStateLabel} tone="neutral" />
                      <StatusBadge label={provider.observedStateLabel} tone={provider.statusTone} />
                    </div>
                  ) : null}
                </div>

                <div className="space-y-6 px-5 py-5">
                  {loginUrl ? (
                    <div className="flex flex-col gap-2 rounded-control border border-border bg-muted/30 px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0">
                        <p className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">{loginUrlLabel}</p>
                        <p className="truncate font-mono text-xs">{loginUrl}</p>
                      </div>
                      <Button type="button" variant="outline" size="sm" className="gap-2" onClick={onCopyLoginUrl}>
                        <Clipboard className="h-3.5 w-3.5" aria-hidden="true" /> Copy
                      </Button>
                    </div>
                  ) : null}

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label htmlFor="tenant-oidc-display-name">Display name</Label>
                      <Input id="tenant-oidc-display-name" value={draft.displayName} onChange={(event) => patch({ displayName: event.target.value })} disabled={!canManage || busy} autoComplete="off" />
                      <FieldError>{validation.displayName}</FieldError>
                    </div>
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label htmlFor="tenant-oidc-issuer">Exact issuer URL</Label>
                      <Input id="tenant-oidc-issuer" type="url" value={draft.issuer} onChange={(event) => patch({ issuer: event.target.value })} disabled={!canManage || busy} spellCheck={false} autoComplete="off" placeholder="https://identity.example.com/realms/company" />
                      <p className="text-xs text-muted-foreground">Discovery must report this exact HTTPS issuer.</p>
                      <FieldError>{validation.issuer}</FieldError>
                    </div>
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label htmlFor="tenant-oidc-client-id">Client ID</Label>
                      <Input id="tenant-oidc-client-id" value={draft.clientId} onChange={(event) => patch({ clientId: event.target.value })} disabled={!canManage || busy} spellCheck={false} autoComplete="off" />
                      <FieldError>{validation.clientId}</FieldError>
                    </div>
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label htmlFor="tenant-oidc-role-claim">Upstream role claim path</Label>
                      <Input id="tenant-oidc-role-claim" value={draft.roleClaimPath} onChange={(event) => patch({ roleClaimPath: event.target.value })} disabled={!canManage || busy} spellCheck={false} autoComplete="off" placeholder="realm_access.roles" />
                      <p className="text-xs text-muted-foreground">Use a dotted claim path whose value is an array of exact role strings.</p>
                      <FieldError>{validation.roleClaimPath}</FieldError>
                    </div>
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label htmlFor="tenant-oidc-additional-scopes">Additional scopes</Label>
                      <Input id="tenant-oidc-additional-scopes" value={draft.additionalScopes} onChange={(event) => patch({ additionalScopes: event.target.value })} disabled={!canManage || busy} spellCheck={false} autoComplete="off" placeholder="groups roles" />
                      <p className="text-xs text-muted-foreground">Space- or comma-separated. The openid scope is always included.</p>
                      <FieldError>{validation.additionalScopes}</FieldError>
                    </div>
                  </div>

                  <div className="space-y-4 border-t border-border pt-5">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2"><KeyRound className="h-4 w-4 text-muted-foreground" aria-hidden="true" /><h3 className="text-sm font-bold">Write-only client secret</h3></div>
                        <p className="mt-1 text-xs text-muted-foreground">Saved secrets are never displayed again.</p>
                      </div>
                      {provider?.secretConfigured ? (
                        <label className="flex items-center gap-2 text-xs font-bold">
                          <Switch checked={draft.replaceSecret} onCheckedChange={(checked) => patch({ replaceSecret: checked, clientSecret: "" })} disabled={!canManage || busy} />
                          Replace secret
                        </label>
                      ) : null}
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="tenant-oidc-client-secret">Client secret</Label>
                      <Input id="tenant-oidc-client-secret" type="password" value={draft.clientSecret} onChange={(event) => patch({ clientSecret: event.target.value })} disabled={!canManage || busy || secretInputDisabled} autoComplete="new-password" />
                      {provider?.secretConfigured && !draft.replaceSecret ? <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" aria-hidden="true" />A client secret is configured.</p> : null}
                      <FieldError>{validation.clientSecret}</FieldError>
                    </div>
                  </div>

                  {clientSettings ? (
                    <Accordion
                      type="single"
                      collapsible
                      defaultValue={clientSettings.defaultExpanded ? "oidc-client-settings" : undefined}
                      className="border-t border-border"
                    >
                      <AccordionItem value="oidc-client-settings" className="border-b-0">
                        <AccordionTrigger className="text-left hover:no-underline">
                          <span>
                            <span className="block text-sm font-bold">{clientSettings.title}</span>
                            <span className="mt-1 block text-xs font-normal text-muted-foreground">{clientSettings.description}</span>
                          </span>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="space-y-4">
                            <div className="flex flex-col gap-3 rounded-control border border-border bg-muted/30 px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
                              <div className="min-w-0">
                                <p className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">{clientSettings.callbackLabel}</p>
                                <p className="mt-1 break-all font-mono text-xs">{clientSettings.callbackUrl}</p>
                              </div>
                              <Button type="button" variant="outline" size="sm" className="shrink-0 gap-2" onClick={onCopyCallbackUrl}>
                                <Clipboard className="h-3.5 w-3.5" aria-hidden="true" /> Copy
                              </Button>
                            </div>
                            <dl className="divide-y divide-border rounded-control border border-border">
                              {clientSettings.settings.map((setting) => (
                                <div key={setting.id} className="grid gap-1 px-3 py-2.5 sm:grid-cols-[minmax(0,12rem)_minmax(0,1fr)] sm:gap-4">
                                  <dt className="text-xs font-bold text-muted-foreground">{setting.label}</dt>
                                  <dd className="min-w-0 text-xs">
                                    <span className={setting.id === "redirect-uri" ? "break-all font-mono" : "break-words"}>{setting.value}</span>
                                    {setting.description ? <span className="mt-1 block text-muted-foreground">{setting.description}</span> : null}
                                  </dd>
                                </div>
                              ))}
                            </dl>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  ) : null}

                  {provider?.safeDiagnostic ? (
                    <div className={cn("rounded-control border px-3 py-3 text-sm", toneClasses[provider.statusTone])} role={provider.statusTone === "danger" ? "alert" : "status"}>{provider.safeDiagnostic}</div>
                  ) : null}
                  <FieldError>{validation.general}</FieldError>
                </div>

                {canManage ? (
                  <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-3">
                    <div className="flex flex-wrap gap-2">
                      {provider ? <Button type="button" variant="outline" onClick={onValidateProvider} disabled={busy}>Validate</Button> : null}
                      {provider && provider.desiredState !== "active" ? <Button type="button" variant="outline" onClick={onActivateProvider} disabled={busy || provider.observedState !== "ready"}>Activate</Button> : null}
                      {provider && provider.desiredState === "active" ? <Button type="button" variant="outline" onClick={onDisableProvider} disabled={busy}>Disable</Button> : null}
                    </div>
                    <div className="flex gap-2">
                      {provider ? <Button type="button" variant="ghost" size="icon" aria-label="Delete identity provider" onClick={onDeleteProvider} disabled={busy}><Trash2 className="h-4 w-4 text-destructive" /></Button> : null}
                      <Button type="button" onClick={onSaveDraft} disabled={busy}>{pendingAction === "save" || pendingAction === "replace_secret" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}{provider ? "Save changes" : "Save draft"}</Button>
                    </div>
                  </div>
                ) : null}
              </section>

              {provider && roleMappings ? (
                <section className="rounded-panel border border-border bg-card" aria-labelledby="tenant-oidc-role-mappings-heading">
                  <div className="border-b border-border px-5 py-4">
                    <p className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Authorization</p>
                    <h2 id="tenant-oidc-role-mappings-heading" className="mt-0.5 text-base font-black">Upstream role mappings</h2>
                    <p className="mt-1 text-xs text-muted-foreground">Exact upstream role values grant the union of permissions from the selected tenant roles for one hour after sign-in.</p>
                  </div>
                  <div className="space-y-3 px-5 py-5">
                    {roleMappings.loading ? <p className="text-sm text-muted-foreground">Loading role mappings…</p> : null}
                    {!roleMappings.loading && roleMappings.rows.length === 0 ? <p className="rounded-control border border-dashed border-border px-3 py-4 text-sm text-muted-foreground">No mappings configured. Existing tenant memberships and invitations continue to control access.</p> : null}
                    {roleMappings.rows.map((row, index) => (
                      <div key={`${index}-${row.tenantRoleId}`} className="grid gap-2 rounded-control border border-border p-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end">
                        <div className="space-y-1.5">
                          <Label htmlFor={`tenant-oidc-external-role-${index}`}>Upstream role</Label>
                          <Input id={`tenant-oidc-external-role-${index}`} value={row.externalRole} onChange={(event) => roleMappings.onChange(roleMappings.rows.map((item, itemIndex) => itemIndex === index ? { ...item, externalRole: event.target.value } : item))} disabled={!roleMappings.canManage || roleMappings.submitting} spellCheck={false} autoComplete="off" />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor={`tenant-oidc-tenant-role-${index}`}>Tenant role</Label>
                          <select id={`tenant-oidc-tenant-role-${index}`} className="flex h-10 w-full rounded-control border border-input bg-background px-3 py-2 text-sm" value={row.tenantRoleId} onChange={(event) => roleMappings.onChange(roleMappings.rows.map((item, itemIndex) => itemIndex === index ? { ...item, tenantRoleId: event.target.value } : item))} disabled={!roleMappings.canManage || roleMappings.submitting}>
                            <option value="">Select a tenant role</option>
                            {roleMappings.availableRoles.map((role) => <option key={role.id} value={role.id}>{role.name} ({role.code})</option>)}
                          </select>
                        </div>
                        <Button type="button" variant="ghost" size="icon" aria-label={`Remove role mapping ${index + 1}`} onClick={() => roleMappings.onChange(roleMappings.rows.filter((_, itemIndex) => itemIndex !== index))} disabled={!roleMappings.canManage || roleMappings.submitting}><X className="h-4 w-4" /></Button>
                      </div>
                    ))}
                  </div>
                  {roleMappings.canManage ? (
                    <div className="flex flex-wrap justify-between gap-2 border-t border-border px-5 py-3">
                      <Button type="button" variant="outline" onClick={() => roleMappings.onChange([...roleMappings.rows, { externalRole: "", tenantRoleId: "" }])} disabled={roleMappings.submitting || roleMappings.rows.length >= 100}>Add mapping</Button>
                      <Button type="button" onClick={roleMappings.onSave} disabled={roleMappings.loading || roleMappings.submitting}>{roleMappings.submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}Save mappings</Button>
                    </div>
                  ) : null}
                </section>
              ) : null}

              {provider ? (
                <section className="grid gap-3 sm:grid-cols-3" aria-label="Provider diagnostics">
                  <div className="rounded-control border border-border bg-card px-3 py-3"><p className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Last validated</p><p className="mt-1 text-sm font-bold">{provider.lastValidatedAt || "Not yet"}</p></div>
                  <div className="rounded-control border border-border bg-card px-3 py-3"><p className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Last login</p><p className="mt-1 text-sm font-bold">{provider.lastSuccessfulLoginAt || "No successful login"}</p></div>
                  <div className="rounded-control border border-border bg-card px-3 py-3"><p className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Secret</p><p className="mt-1 text-sm font-bold">{provider.secretConfigured ? "Configured" : "Required"}</p></div>
                </section>
              ) : null}
            </>
          )}
        </div>
      </ContentFrame>

      <DialogPrimitive.Root open={Boolean(confirmation)} onOpenChange={(open) => { if (!open) confirmation?.onCancel(); }}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-foreground/30" />
          <DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-panel border border-border bg-background shadow-xl focus:outline-none">
            <div className="flex items-start justify-between gap-4 border-b border-border px-5 py-4">
              <div><DialogPrimitive.Title className="text-base font-black">{confirmation?.title}</DialogPrimitive.Title><DialogPrimitive.Description className="mt-1 text-sm text-muted-foreground">{confirmation?.description}</DialogPrimitive.Description></div>
              <DialogPrimitive.Close asChild><Button type="button" variant="ghost" size="icon" aria-label="Close confirmation"><X className="h-4 w-4" /></Button></DialogPrimitive.Close>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4"><Button type="button" variant="ghost" onClick={confirmation?.onCancel}>Cancel</Button><Button type="button" variant={confirmation?.destructive ? "destructive" : "default"} onClick={confirmation?.onConfirm} disabled={busy}>{pendingAction === confirmation?.kind ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}{confirmation?.confirmLabel}</Button></div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </div>
  );
}
